/**
 * Type definitions for the Monthly Feed Budget Planner.
 *
 * Structure:
 * - FeedBudgetPlan: whole-farm plan for a year
 *   - grazingMonths: farm-level monthly grazing config (growth rate, utilisation)
 *   - animalGroupBudgets[]: one per animal group, each containing:
 *     - stock classes with monthly entries
 *     - assigned grazing area (from fields)
 *     - supplement plans
 */

// ===== Plan-level types =====

export interface FeedBudgetPlan {
  guid: string;
  farm: string;
  name: string;
  /** @deprecated use startDate/endDate */
  planYear?: number;
  /** ISO date string: first day of the start month, e.g. "2025-01-01" */
  startDate: string;
  /** ISO date string: last day of the end month, e.g. "2025-12-31" */
  endDate: string;
  initialPastureCoverKgHa: number;
  targetResidualKgHa: number;
  /** Farm-wide monthly defaults (growth rate, utilisation) */
  grazingMonths: MonthlyGrazingConfig[];
  /** Farm-wide supplement substitution assumptions */
  supplementSubstitutionRates: SupplementSubstitutionRates;
  /** One budget per animal group */
  animalGroupBudgets: AnimalGroupBudget[];
}

export interface SupplementSubstitutionRates {
  silage: number;
  hay: number;
  concentrates: number;
}

export interface SupplementFeedType {
  id: string;
  name: string;
  substitutionRate: number;
}

export interface MonthlyGrazingConfig {
  month: number; // 0-11
  /** 0 = plan's first year (default), 1 = plan's second year */
  yearOffset?: number;
  growthRateKgHaDay: number;
  utilisationPercent: number;
}

// ===== Per-animal-group types =====

export interface AnimalGroupBudget {
  animalGroupId: string;
  animalGroupName: string;
  /** Grazing area in hectares — base/default value */
  grazingAreaHa: number;
  /** Per-month area overrides (0-11 → ha). Falls back to grazingAreaHa if absent. */
  monthlyGrazingAreaHa?: Record<number, number>;
  stockClassPlans: StockClassMonthlyPlan[];
  supplementFeedTypes: SupplementFeedType[];
  supplementPlans: MonthlySupplementPlan[];
  /** Per-feed-type utilisation % when feeding in a housed system (feedTypeId → 0-100) */
  feedTypeUtilisation?: Record<string, number>;
  /** Utilisation % for grass when animals are field grazing (0-100) */
  grazingUtilisationPercent?: number;
  /** Utilisation % for supplements when field feeding / bale grazing (0-100) */
  fieldFeedingUtilisationPercent?: number;
}

export interface StockClassMonthlyPlan {
  stockClassId: string;
  stockClassName: string;
  defaultStockClassName?: string;
  months: StockClassMonthEntry[];
}

export interface StockClassMonthEntry {
  month: number; // 0-11
  animalCount: number;
  averageWeightKg: number;
  allocationPercent: number; // % of body weight
  isGrazing?: boolean; // defaults to true
  /** 0 = plan's first year (default), 1 = plan's second year. Used for spawned
   *  classes whose animals appear after the plan's first 12 months. */
  yearOffset?: number;
}

export interface MonthlySupplementPlan {
  month: number; // 0-11
  utilisationPercent: number;
  /** feedTypeId → tonnes DM */
  amounts: Record<string, number>;
  /** @deprecated use amounts */
  hayTonnesDM?: number;
  /** @deprecated use amounts */
  silageTonnesDM?: number;
  /** @deprecated use amounts */
  concentratesTonnesDM?: number;
}

// ===== Computed types (derived client-side) =====

export interface FarmTotalComputedRow {
  month: number;
  growthRateKgHaDay: number;
  utilisationPercent: number;
  totalGrazingAreaHa: number;
  totalDemandKgDay: number;
  totalSupplyKgDay: number;
  demandKgHaDay: number;
  supplyKgHaDay: number;
  differenceKgHaDay: number;
  totalSupplementKgDay: number;
  netDifferenceKgHaDay: number;
  averagePastureCoverKgHa: number;
}

export type HousingStatus = "grazing" | "partially-grazing" | "housed";

export interface AnimalGroupComputedRow {
  month: number;
  grazingAreaHa: number;
  totalDemandKgDay: number;
  demandKgHaDay: number;
  supplyKgHaDay: number;
  differenceKgHaDay: number;
  supplementKgHaDay: number;
  netDifferenceKgHaDay: number;
  housingStatus: HousingStatus;
  /** % of total demand covered by grass forage alone (capped at 999 when demand ≈ 0) */
  forageCoveragePercent: number;
}

export interface StockClassComputedMonth {
  month: number;
  animalCount: number;
  averageWeightKg: number;
  allocationPercent: number;
  dailyAllocationKgDM: number;
  totalDailyRequirementKgDM: number;
}

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export const MONTH_LABELS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
